import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Card,
  Descriptions,
  Flex,
  Grid,
  Modal,
  Typography,
  message,
} from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useState } from "react";
import { format } from "date-fns";
import pallete from "../../../../utils/pallete";
import { rccPaymentProofService } from "../../../../services/rcc-payment-proof.service";

export default function RccPaymentProofDetailContent({ id, isFromRegistration = false, onActionSuccess }) {
  const breakpoint = Grid.useBreakpoint();
  const [previewImage, setPreviewImage] = useState("");
  const [loadingVerify, setLoadingVerify] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [messageApi, messageHolder] = message.useMessage();

  const { data: apiData, isLoading } = useQuery({
    queryKey: ["rccPaymentProofDetail", id],
    queryFn: () => rccPaymentProofService.getById(id),
    enabled: !!id,
  });

  const data = apiData?.data;

  const handleVerify = async () => {
    try {
      setLoadingVerify(true);
      await rccPaymentProofService.verify(id, { actor: "currentUser" });
      messageApi.success("Verifikasi berhasil!");
      if (onActionSuccess) {
        onActionSuccess('verified');
      }
    } catch (error) {
      messageApi.error(
        error.response?.data?.message || "Verifikasi gagal. Silakan coba lagi."
      );
    } finally {
      setLoadingVerify(false);
    }
  };

  const handleRejectConfirm = async () => {
    try {
      setRejectModalOpen(false);
      await rccPaymentProofService.reject(id, { actor: "currentUser" });
      messageApi.success("Ajuan berhasil ditolak.");
      if (onActionSuccess) {
        onActionSuccess('rejected');
      }
    } catch (error) {
      messageApi.error(
        error.response?.data?.message || "Cancel gagal. Silakan coba lagi."
      );
    }
  };

  const handleDownload = async (url) => {
    try {
      const response = await fetch(url, {
        mode: "cors",
      });
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = url.split("/").pop()?.split("\\").pop() || "download";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Download gagal:", error);
      window.open(url, '_blank');
    }
  };

  const renderFileAction = (label, filePath) => {
    if (!filePath) return "-";
    const cleanedPath = filePath.replace(/\\/g, "/");
    const filename = cleanedPath.split("/").pop();
    console.log(cleanedPath)
    console.log(filename)

    return (
      <Flex gap={8} align="center">
        <Typography.Text style={{ marginRight: "auto" }}>
          {filename}
        </Typography.Text>
        <Flex vertical={!breakpoint.lg} gap={8}>
          <Button
            icon={
              <Icon
                icon="solar:download-square-bold-duotone"
                width="18"
                height="18"
                style={{ marginBottom: -1 }}
              />
            }
            variant="filled"
            color="primary"
            size="small"
            style={{ fontSize: 12 }}
            onClick={() => handleDownload(cleanedPath)}
          >
            Unduh
          </Button>

          <Button
            variant="filled"
            color="primary"
            size="small"
            style={{ fontSize: 12 }}
            icon={
              <Icon
                icon="solar:eye-bold-duotone"
                width="18"
                height="18"
                style={{ marginBottom: -1 }}
              />
            }
            onClick={() => {
              if (cleanedPath.toLowerCase().endsWith(".pdf")) {
                window.open(cleanedPath, '_blank');
              } else {
                setPreviewImage(cleanedPath);
              }
            }}
          >
            Lihat
          </Button>
        </Flex>
      </Flex>
    );
  };

  if (isLoading) return <Typography.Text>Loading...</Typography.Text>;
  if (!id) return <Typography.Text>ID tidak ditemukan</Typography.Text>;
  if (!data) return <Typography.Text>Data tidak ditemukan</Typography.Text>;

  return (
    <>
      {messageHolder}
      <Card title={isFromRegistration ? null : "Detail Bukti Pembayaran RCC"} bordered={!isFromRegistration}>
        <Descriptions
          style={{ marginBottom: 20 }}
          column={1}
          bordered
          size="small"
          layout={breakpoint.lg ? "horizontal" : "vertical"}
          items={[
            {
              label: "Tracking ID",
              children: data?.registration?.tracking_id || "-",
            },
            {
              label: "Tanggal Dibuat",
              children: data?.createdAt
                ? format(new Date(data.createdAt), "dd/MM/yyyy - HH.mm")
                : "-",
            },
            {
              label: "Bukti Bayar",
              children: renderFileAction("Bukti Bayar", data?.paymentProof),
            },
            {
              label: "Formulir Approval",
              children: renderFileAction("Formulir Approval", data?.approvalForm),
            },
          ]}
        />
        {data?.status !== "updated" && data?.status !== "cancel" ? (
          <Flex justify="flex-end" gap={12} style={{ marginTop: 24 }}>
            <Button
              danger
              style={{
                backgroundColor: pallete.danger.main,
                color: "#fff",
                fontSize: 14,
              }}
              onClick={() => setRejectModalOpen(true)}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              style={{
                backgroundColor: pallete.curiousBlue[500],
                color: "#fff",
                fontSize: 14,
              }}
              onClick={handleVerify}
              loading={loadingVerify}
            >
              Update Status
            </Button>
          </Flex>
        ) : null}
      </Card>

      <Modal
        open={previewImage.length > 0}
        centered
        title="Gambar"
        onCancel={() => setPreviewImage("")}
        footer={[
          <Button
            key="cancel"
            variant="outlined"
            color="primary"
            onClick={() => setPreviewImage("")}
          >
            Tutup
          </Button>,
        ]}
      >
        <embed
          style={{
            width: "100%",
            height: "auto",
          }}
          src={previewImage}
        />
      </Modal>

      <Modal
        open={rejectModalOpen}
        centered
        footer={null}
        onCancel={() => setRejectModalOpen(false)}
      >
        <Flex
          vertical
          align="center"
          justify="center"
          style={{ textAlign: "center", padding: "20px 0" }}
        >
          <Icon
            icon="solar:trash-bin-2-bold-duotone"
            width="128"
            height="128"
            style={{ color: pallete.danger.main, marginBottom: 16 }}
          />
          <Typography.Title level={4}>Konfirmasi</Typography.Title>
          <Typography.Text>
            Apakah Anda yakin akan menolak / cancel data ini?
          </Typography.Text>
          <Flex gap={12} style={{ marginTop: 24 }}>
            <Button onClick={() => setRejectModalOpen(false)}>Batal</Button>
            <Button
              danger
              type="primary"
              onClick={handleRejectConfirm}
              style={{ backgroundColor: pallete.danger.main }}
            >
              Ya, Cancel
            </Button>
          </Flex>
        </Flex>
      </Modal>
    </>
  );
}
